import React, { useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import {
	ChatReducerState,
	ChatMessage,
	EMessageTypes,
	EventsMessage,
	GeneralEventsMessage,
} from '../../stores/reducers/chat';
import { SessionState } from '../../stores/reducers/session';
import * as ChatActions from '../../stores/actions/chat';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import ChatMessageRender from './comment';
import usePrevious from '../../utils/use-previous';
import useScrolledToBottom from '../../hooks/use-scrolled-to-bottom';
import { TabsState } from '../../stores/reducers/tabs';
import { TABS } from '../../stores/types/tabs';

interface OwnProps {
}

interface StateProps {
	// the reducer you want to import into this component
	ChatReducer: ChatReducerState;
	SessionReducer: SessionState;
	TabsReducer: TabsState;
}

interface DispatchProps {
	// the actions you want to fire from this component
	goBack: () => void;
	markLegacyAsRead: () => void;
	markEventsMessagesAsRead: (
		token: string,
		meeting_id: string,
		chatMessages: number[],
		questions: number[],
		comments: number[]
	) => void;
}

const mapStateToProps = (state: any, ownProps: any) => ({ ...state, ...ownProps });

const mapDispatchToProps = (dispatch: Function) =>
	// the actions you want to fire from this component
	// eslint-disable-next-line implicit-arrow-linebreak
	({
		goBack: () => {
			dispatch(ChatActions.viewModeratedComments(false));
		},
		markLegacyAsRead: () => {
			dispatch(ChatActions.setModeratedLegacyCommentsSeen());
		},
		markEventsMessagesAsRead: (
			token: string,
			// eslint-disable-next-line camelcase
			meeting_id: string,
			chatMessages: number[],
			questions: number[],
			comments: number[],
		) => {
			dispatch(
				ChatActions.setModeratedEventsMessagesSeen(
					token,
					meeting_id,
					chatMessages,
					questions,
					comments,
				),
			);
		},
	});
function sortByDate(a: ChatMessage, b: ChatMessage) {
	return a.received.valueOf() < b.received.valueOf() ? 1 : -1;
}

type Props = StateProps & DispatchProps & OwnProps;

function ModeratedComments(props: Props) {
	const {
		ChatReducer: {
			legacyModeratedComments,
			moderatedEventsMessages,
		},
		SessionReducer: {
			meeting,
			token,
		},
		TabsReducer: {
			activeTab,
		},
		goBack,
		markLegacyAsRead,
		markEventsMessagesAsRead,
	} = props;

	const container = useRef<HTMLDivElement | null>(null);

	const chatMessageArray = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const eventsMessages = Array.from(moderatedEventsMessages, ([name, value]) => (value));
		let legacyEventsMessages = Array.from(Object.values(legacyModeratedComments));

		if (eventsMessages.length) {
			return eventsMessages;
		}

		if (legacyEventsMessages.length) {
			legacyEventsMessages = legacyEventsMessages
				.filter((comm: ChatMessage) => !comm.deleted && comm.elevated)
				.sort(sortByDate);
		}

		return legacyEventsMessages;
	}, [legacyModeratedComments, moderatedEventsMessages]);

	const prevChatMessageArray = usePrevious(chatMessageArray);
	const prevActiveTab = usePrevious(activeTab);
	const { scrolledToBottom } = useScrolledToBottom({ scrolledContainerRef: container?.current });

	const scrollToBottom = () => {
		// scroll to bottom of the chat window if the user:
		// - previously scrolled to the bottom and a new message is sent or received
		// - entered a new chat room
		// - changed tabs
		if (
			container.current
			&& (
				(prevChatMessageArray?.length !== chatMessageArray?.length
				&& scrolledToBottom)
				|| !prevChatMessageArray
				|| prevActiveTab !== TABS.CHAT
			)
		) {
			container.current?.scrollTo({
				top: container.current.scrollHeight,
				behavior: 'auto',
			});
		}
	};

	// resizeObserver is used to observe the final size of the container after it finishes
	// animating in, so we can then scroll to the bottom (if necessary).
	const resizeObserver = new ResizeObserver(() => { scrollToBottom(); });

	useEffect(() => {
		if (container.current) {
			resizeObserver.observe(container.current);

			setTimeout(() => {
				// we are only observing container resizes in order to scroll to the bottom
				// after it animates in, so disconnect the observer shortly after.
				if (container.current) { resizeObserver.disconnect(); }
			}, 500);
		}
		return () => { if (container.current) { resizeObserver.disconnect(); } };
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [chatMessageArray]);

	useEffect(() => {
		// This will mark all moderated comments as read when "Events Q&A" in Chat
		// tab is selected
		if (moderatedEventsMessages.size && token && meeting?.meeting_id) {
			const chatMessages: number[] = [];
			const questions: number[] = [];
			const comments: number[] = [];

			// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-restricted-syntax
			for (const eventsMessage of Array.from(moderatedEventsMessages, ([name, value]) => (value))) {
				// eslint-disable-next-line camelcase
				const { id, message_type, seen } = eventsMessage;

				if (!seen) {
					// eslint-disable-next-line camelcase
					switch (message_type) {
						case EMessageTypes.Chat:
							chatMessages.push(id);
							break;
						case EMessageTypes.Question:
							questions.push(id);
							break;
						case EMessageTypes.QuestionComment:
							comments.push(id);
							break;
						default:
							break;
					}
				}
			}

			if (chatMessages.length || questions.length || comments.length) {
				markEventsMessagesAsRead(token, meeting.meeting_id, chatMessages, questions, comments);
			}
		} else if (chatMessageArray.length) {
			markLegacyAsRead();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		chatMessageArray.length,
		markEventsMessagesAsRead,
		markLegacyAsRead,
		meeting,
		moderatedEventsMessages.size,
		token,
	]);

	function getId(
		message: ChatMessage | GeneralEventsMessage,
	) {
		const { id } = message as EventsMessage;
		// eslint-disable-next-line camelcase
		const { chat_message } = message as ChatMessage;

		// eslint-disable-next-line camelcase
		return id ?? chat_message;
	}

	return (
		<div className="room-wrapper height-full">
			<button className="chat-back-button" onClick={goBack}>
				<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={17}></Icon>
				<span>All</span>
			</button>
			<div className="chat-container comments overflow-scroll-y" ref={container}>
				<div className="messages-container">
					{chatMessageArray.map((chatMessage, index) => (
						<ChatMessageRender message={chatMessage} key={getId(chatMessage) ?? index} />
					))}
				</div>
			</div>

		</div>
	);
}

export default connect<StateProps, DispatchProps, OwnProps>(
	mapStateToProps,
	mapDispatchToProps,
)(ModeratedComments);
